import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import {format} from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDOB(date: Date | string | undefined): string {
  if (!date) return ""

  const parsedDate = typeof date === "string" ? new Date(date) : date;

  if (isNaN(parsedDate.getTime())) {
    return ""
  }

  return format(parsedDate, "MM/dd/yyyy")
}


export function formatDate(date: unknown, df: string = "dd-MM-yy h:mm a"): string {
  try {
    if (!date) return "";

    let parsed: Date;

    // Date-only strings like "2026-05-14" are parsed by new Date() as UTC midnight,
    // which shifts the displayed day by -1 in negative UTC offset timezones (e.g. EST).
    // Detect them and parse as local midnight instead.
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-").map(Number);
      parsed = new Date(year, month - 1, day); // local midnight — no UTC shift
    } else {
      parsed = new Date(date as string | number | Date);
    }

    if (isNaN(parsed.getTime())) return "";
    return format(parsed, df);
  } catch (error) {
    console.log(error);
    return "...";
  }
}

export function calculateAge(date: unknown): number {
  try {
    if (!date) return 0;

    const birthDate = new Date(date as string | number | Date);
    if (isNaN(birthDate.getTime())) return 0;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();

    const hasHadBirthdayThisYear =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    if (!hasHadBirthdayThisYear) {
      age--;
    }

    return age;
  } catch {
    return 0;
  }
}

export function getGenderInitial(gender: string | null | undefined): string {
  if (!gender) return "";
  return gender.trim().charAt(0).toUpperCase(); // 'm' → 'M', 'f' → 'F'
}

export function maskEmail(email: string | null | undefined): string {
  if (!email) return "";
  return email;
}

export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  return phone;
}

export function resolveStateName(loc: any, item: any = {}): string {
  const explicit = loc?.state_name || loc?.stateName || item?.state_name || item?.stateName;
  if (explicit) return explicit;
  
  if (typeof loc?.state === 'string') return loc.state;
  if (loc?.state?.name) return loc.state.name;
  if (typeof item?.state === 'string') return item.state;
  if (item?.state?.name) return item.state.name;
  
  // FIX: Added Virginia state_id to match the states table seed data
  const stateIdMap: Record<string, string> = {
    '1cdca5f6-4e73-44a7-b33b-1b387b6b5195': 'Alabama',
    '8360f636-18a9-4c88-a2f7-dca91032ed74': 'South Carolina',
    '01be12e6-8a09-4ab2-a3c2-91345eb191ed': 'Tennessee',
    '5fbe5093-7004-498c-a1bf-d0fcbb31e810': 'Virginia',
  };
  
  const sId = loc?.state_id || item?.state_id;
  if (sId && stateIdMap[sId]) return stateIdMap[sId];
  
  if (loc?.address) {
    const parts = String(loc.address).split(',');
    const lastPart = parts[parts.length - 1]?.trim().toUpperCase();
    if (lastPart === 'AL') return 'Alabama';
    if (lastPart === 'SC') return 'South Carolina';
    if (lastPart === 'TN') return 'Tennessee';
    if (lastPart === 'VA') return 'Virginia';
    if (lastPart === 'CA') return 'California';
    if (lastPart === 'AZ') return 'Arizona';
    if (lastPart === 'MO') return 'Missouri';
  }
  
  return item?.name || "Other";
}

/**
 * Maps US state names to IANA timezone identifiers.
 * Used by the appointment form to compute the correct clinic-local
 * default time and past-appointment validation when a location is selected.
 * Must stay in sync with _STATE_TIMEZONE_MAP in rds_routes/appointments.py.
 */
export const STATE_TIMEZONE_MAP: Record<string, string> = {
  "Alabama":              "America/Chicago",
  "Alaska":               "America/Anchorage",
  "Arizona":              "America/Phoenix",
  "Arkansas":             "America/Chicago",
  "California":           "America/Los_Angeles",
  "Colorado":             "America/Denver",
  "Connecticut":          "America/New_York",
  "Delaware":             "America/New_York",
  "Florida":              "America/New_York",
  "Georgia":              "America/New_York",
  "Hawaii":               "Pacific/Honolulu",
  "Idaho":                "America/Boise",
  "Illinois":             "America/Chicago",
  "Indiana":              "America/Indiana/Indianapolis",
  "Iowa":                 "America/Chicago",
  "Kansas":               "America/Chicago",
  "Kentucky":             "America/Kentucky/Louisville",
  "Louisiana":            "America/Chicago",
  "Maine":                "America/New_York",
  "Maryland":             "America/New_York",
  "Massachusetts":        "America/New_York",
  "Michigan":             "America/Detroit",
  "Minnesota":            "America/Chicago",
  "Mississippi":          "America/Chicago",
  "Missouri":             "America/Chicago",
  "Montana":              "America/Denver",
  "Nebraska":             "America/Chicago",
  "Nevada":               "America/Los_Angeles",
  "New Hampshire":        "America/New_York",
  "New Jersey":           "America/New_York",
  "New Mexico":           "America/Denver",
  "New York":             "America/New_York",
  "North Carolina":       "America/New_York",
  "North Dakota":         "America/Chicago",
  "Ohio":                 "America/New_York",
  "Oklahoma":             "America/Chicago",
  "Oregon":               "America/Los_Angeles",
  "Pennsylvania":         "America/New_York",
  "Rhode Island":         "America/New_York",
  "South Carolina":       "America/New_York",
  "South Dakota":         "America/Chicago",
  "Tennessee":            "America/Chicago",
  "Texas":                "America/Chicago",
  "Utah":                 "America/Denver",
  "Vermont":              "America/New_York",
  "Virginia":             "America/New_York",
  "Washington":           "America/Los_Angeles",
  "West Virginia":        "America/New_York",
  "Wisconsin":            "America/Chicago",
  "Wyoming":              "America/Denver",
  "District of Columbia": "America/New_York",
};

/**
 * Given a stateName string, returns the IANA timezone or
 * "America/New_York" as a safe fallback.
 */
export function getTimezoneForState(stateName: string | undefined | null): string {
  if (!stateName) return "America/New_York";
  return STATE_TIMEZONE_MAP[stateName] ?? "America/New_York";
}

/**
 * Returns the current "now" Date as seen in a given IANA timezone.
 * Used to compute clinic-local default date/time in the appointment form.
 */
export function getNowInTimezone(ianaTimezone: string): Date {
  try {
    // Intl gives us the local time string in that zone; parse it back to a Date.
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: ianaTimezone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }).formatToParts(new Date());

    const get = (t: string) => parts.find(p => p.type === t)?.value ?? "0";
    const year   = parseInt(get("year"));
    const month  = parseInt(get("month")) - 1;
    const day    = parseInt(get("day"));
    let   hour   = parseInt(get("hour"));
    const minute = parseInt(get("minute"));
    const second = parseInt(get("second"));

    // Intl hour12:false can return 24 for midnight — normalise to 0
    if (hour === 24) hour = 0;

    return new Date(year, month, day, hour, minute, second, 0);
  } catch {
    // Fallback to browser local time if timezone is unrecognised
    return new Date();
  }
}

export function validateNPI(npi: string | null | undefined): boolean {
  if (!npi || String(npi).trim() === "") return true; // Optional by default
  const npiStr = String(npi).trim();
  
  // 1. Must be 10 numeric digits
  // 2. First digit must be 1 or 2
  if (!/^[12]\d{9}$/.test(npiStr)) return false;
  
  // 3. Last digit is a checksum (Luhn-80)
  // The prefix for US NPI is 80840. The sum of 80840 in Luhn is 24.
  let sum = 24;
  for (let i = 0; i < 9; i++) {
    let digit = parseInt(npiStr[i]);
    // Positions 0, 2, 4, 6, 8 (1st, 3rd, 5th, 7th, 9th digits) are multiplied by 2
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(npiStr[9]);
}

/**
 * Formats a raw mobile_number from the DB into a consistent readable format for DISPLAY.
 * Handles: "+18042544103", "18042544103", "+118042544103", "8042544103", "(804) 254-4103"
 * Output:  "+1 (804) 254-4103"
 */
export function formatPhoneDisplay(raw: string | null | undefined): string {
  if (!raw) return "";
  const str = String(raw).trim();
  let digits = str.replace(/\D/g, "");

  // Strip leading country-code "1"s (handles "+11...", "+1...", "1804...")
  while (digits.length > 10 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  if ((str.startsWith("+1") || str.startsWith("+")) && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  // If we don't have exactly 10 digits, return raw as fallback
  if (digits.length !== 10) return raw;
  return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Formats user input as a 10-digit US phone number: (###) ###-####
 * Safely handles typing, backspacing, re-editing, and pasting (+1, 11 digits, etc.)
 */
export function formatPhoneInput(raw: string | null | undefined): string {
  if (!raw) return "";
  const str = String(raw).trim();
  let digits = str.replace(/\D/g, "");

  // Strip leading country-code 1s
  while (digits.length > 10 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  if ((str.startsWith("+1") || str.startsWith("+")) && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  digits = digits.slice(0, 10);
  if (!digits) return "";

  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Formats user input as a 10-digit US phone number with dashes: ###-###-####
 * Used when an external "+1" prefix badge is rendered beside the input.
 */
export function formatPhoneDash(raw: string | null | undefined): string {
  if (!raw) return "";
  const str = String(raw).trim();
  let digits = str.replace(/\D/g, "");

  // Strip leading country-code 1s
  while (digits.length > 10 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  if ((str.startsWith("+1") || str.startsWith("+")) && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  digits = digits.slice(0, 10);
  if (!digits) return "";

  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Preserves cursor position across phone formatting shifts so re-editing
 * and backspacing in the middle of inputs work seamlessly.
 */
export function handlePhoneInputChange(
  e: React.ChangeEvent<HTMLInputElement>,
  onChange: (val: string) => void,
  formatFn: (val: string) => string = formatPhoneInput
) {
  const input = e.target;
  const rawValue = input.value;
  const selectionStart = input.selectionStart ?? rawValue.length;

  // Count how many numeric digits were before the cursor before formatting
  const digitsBeforeCursor = rawValue.slice(0, selectionStart).replace(/\D/g, "").length;

  const formatted = formatFn(rawValue);
  input.value = formatted;
  onChange(formatted);

  // Position cursor after the same number of digits in the formatted string
  requestAnimationFrame(() => {
    let newCursorPos = formatted.length;
    let digitCount = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        digitCount++;
      }
      if (digitCount === digitsBeforeCursor) {
        newCursorPos = i + 1;
        break;
      }
    }
    if (digitsBeforeCursor === 0) {
      newCursorPos = formatted.startsWith("(") ? 1 : 0;
    }
    input.setSelectionRange(newCursorPos, newCursorPos);
  });
}

/**
 * Smart backspace handler so that backspacing over formatting characters
 * (spaces, hyphens, parentheses) naturally deletes the preceding digit.
 */
export function handlePhoneKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === "Backspace") {
    const input = e.currentTarget;
    const { selectionStart, selectionEnd, value } = input;
    // Only if single cursor without highlighted range
    if (selectionStart !== null && selectionStart === selectionEnd && selectionStart > 0) {
      const charBefore = value[selectionStart - 1];
      // If the character directly before the cursor is a formatting symbol
      if (!/\d/.test(charBefore)) {
        let prevDigitIndex = -1;
        for (let i = selectionStart - 1; i >= 0; i--) {
          if (/\d/.test(value[i])) {
            prevDigitIndex = i;
            break;
          }
        }
        if (prevDigitIndex !== -1) {
          e.preventDefault();
          // Remove the character and preceding formatting symbols
          const newValue = value.slice(0, prevDigitIndex) + value.slice(selectionStart);
          input.value = newValue;
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    }
  }
}

/**
 * Returns a patient-safe public base URL.
 * NEVER returns localhost or 127.0.0.1 so links sent to patients always use
 * the public domain (dev or prod).
 */
export function getPublicAppBaseUrl(): string {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.startsWith("192.168.") ||
      host.startsWith("10.") ||
      host.endsWith(".local");

    if (!isLocal) {
      return window.location.origin;
    }
  }

  const envPublic =
    (typeof window !== "undefined" && (window as any)._env_?.VITE_PUBLIC_FRONTEND_URL) ||
    (typeof window !== "undefined" && (window as any)._env_?.VITE_BASE_URL) ||
    import.meta.env.VITE_PUBLIC_FRONTEND_URL ||
    import.meta.env.VITE_BASE_URL;

  if (envPublic && !envPublic.includes("localhost") && !envPublic.includes("127.0.0.1")) {
    return envPublic.replace(/\/$/, "");
  }

  return "https://survey-dev.monitorhealth.ai";
}

/**
 * Format clinic phone as (###) ###-####
 */
export function formatClinicPhone(rawPhone?: string | null): string {
  if (!rawPhone) return "(555) 000-0004";
  const digits = String(rawPhone).replace(/\D/g, "");
  const cleanDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (cleanDigits.length === 10) {
    return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6)}`;
  }
  return rawPhone;
}


